import { useCallback, useEffect, useRef, useState } from "react";
import {
  CopilotApiError,
  createCopilotConversationAPI,
  fetchCopilotFaqAPI,
  fetchCopilotFaqsAPI,
  fetchCopilotMetaAPI,
  sendCopilotFaqFeedbackAPI,
  fetchCopilotUnreadAPI,
  fetchCopilotPreferencesAPI,
  getCopilotConversationAPI,
  listCopilotConversationsAPI,
  markCopilotReadAPI,
  redeemCopilotEmailLinkAPI,
  sendCopilotMessageAPI,
  updateCopilotPreferencesAPI,
  uploadCopilotAttachmentAPI,
} from "../../utils/api/copilot";
import { fetchCopyRestaurantsAPI } from "../../utils/api/menu";
import { getPolicy } from "../../access/core/policy";
import { menuPermissions } from "../../access/modules/menu";
import {
  getGuestToken,
  listGuestConversations,
  removeGuestConversation,
  saveGuestConversation,
} from "../../utils/copilotGuestStore";
import { COPILOT_TEXT, CATEGORY_FALLBACK, errorMessage } from "./copilotText";
import { GUIDED_TEXT } from "./copilotGuidedText";
import { EMPTY_INTAKE, STEPS, MESSAGE_MAX, applicableSteps, cleanMessage, cleanName, editableSteps, hasProgress, normalizeEmail, normalizePhone, persistable } from "./intakeModel";
import { clearIntakeDraft, faqSessionId, loadIntakeDraft, saveIntakeDraft } from "./copilotDraftStore";

// All state and behavior of the Copilot widget. Pure API messaging (no live
// connection): data is loaded when the panel opens, when switching
// conversations, when the page becomes visible again, and by a light poll
// only while the panel is open and the page visible. Everything funnels
// through refreshChat / loadList / refreshUnread, which a future real-time
// transport can call instead of the timers.
//
//   mode "user"    signed in and verified: session token, server-side list
//   mode "guest"   signed out: per-conversation guest tokens (copilotGuestStore)
//   mode "pending" session token present but /me not confirmed yet: no calls

const POLL_OPEN_MS = 10000;
const POLL_CLOSED_MS = 60000;
const FOCUS_MIN_MS = 15000;
const PAGE = 20;
const GUEST_BADGE_MAX = 5;
const FAQ_REFRESH_MS = 5 * 60 * 1000;
export const MAX_FILES = 5;
export const MAX_FILE_BYTES = 5 * 1024 * 1024;
export const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

const uuid = () =>
  window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;

const mergeMessages = (current, incoming) => {
  const byId = new Map(current.map((m) => [m.id, m]));
  incoming.forEach((m) => byId.set(m.id, m));
  return [...byId.values()].sort((a, b) => a.seq - b.seq);
};

const byActivity = (a, b) => new Date(b.lastActivityAt) - new Date(a.lastActivityAt);
const EMPTY_LIST = { items: [], status: "idle", error: null, hasMore: false, cursor: null, loadingMore: false };
const EMPTY_DRAFT = { text: "", files: [] };

export function useCopilotController({ mode, userKey, lang, user, open, visible }) {
  const t = COPILOT_TEXT[lang] || COPILOT_TEXT.en;
  const g = GUIDED_TEXT[lang] || GUIDED_TEXT.en;
  const [view, setView] = useState("home"); // home | faq | intake | list | chat | settings
  const [activeId, setActiveId] = useState(null);
  const [list, setList] = useState(EMPTY_LIST);
  const [chats, setChats] = useState({});
  const [drafts, setDrafts] = useState({});
  const [unread, setUnread] = useState(0);
  const [meta, setMeta] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [notice, setNotice] = useState(null);
  const [faq, setFaq] = useState({ status: "idle", categories: [], items: [], error: null });
  const [faqNav, setFaqNav] = useState({ category: null, key: null, outcome: null });
  const [faqDetails, setFaqDetails] = useState({});
  const [intake, setIntakeState] = useState(EMPTY_INTAKE);
  const [intakeFiles, setIntakeFiles] = useState([]);
  const [creating, setCreating] = useState(false);
  const [prefs, setPrefs] = useState({ status: "idle", email: true, inApp: true, error: null });

  const epoch = useRef(0);
  const busy = useRef(new Set());
  const pendingStore = useRef({});
  const draftsRef = useRef({});
  const createKey = useRef(null);
  const creatingRef = useRef(false);
  const intakeRef = useRef(EMPTY_INTAKE);
  const intakeFilesRef = useRef([]);
  const faqLoadedAt = useRef(0);
  const lastFocusAt = useRef(0);
  const live = useRef({});
  const identity = `${mode}:${userKey || ""}`;
  live.current = { mode, view, activeId, open, visible, t, g, list, lang, identity, user, meta, restaurants };

  // ---- helpers ------------------------------------------------------------
  const stale = (started) => started !== epoch.current;
  const tokenFor = (id) => (live.current.mode === "guest" ? getGuestToken(id) : undefined);

  const patchChat = useCallback((id, patch) => {
    setChats((prev) => ({ ...prev, [id]: { ...(prev[id] || { messages: [], pending: [] }), ...(typeof patch === "function" ? patch(prev[id]) : patch) } }));
  }, []);

  const patchListItem = useCallback((id, patch) => {
    setList((prev) => {
      const items = prev.items.map((c) => (c.id === id ? { ...c, ...patch } : c)).sort(byActivity);
      return { ...prev, items };
    });
  }, []);

  const setDraft = useCallback((id, patch) => {
    const next = { ...(draftsRef.current[id] || EMPTY_DRAFT), ...patch };
    draftsRef.current = { ...draftsRef.current, [id]: next };
    setDrafts(draftsRef.current);
  }, []);

  const expire = useCallback((id) => {
    removeGuestConversation(id);
    patchChat(id, { status: "expired" });
    setList((prev) => ({ ...prev, items: prev.items.filter((c) => c.id !== id) }));
  }, [patchChat]);

  // ---- reset on identity change (login / logout / user switch) ---------------
  const previousIdentity = useRef(identity);
  const wasActive = useRef(false);
  useEffect(() => {
    // Only a real identity change resets (the first run, and StrictMode's repeat of
    // it, find the initial state already in place; resetting there would also
    // discard work started by a deep link).
    if (previousIdentity.current === identity) return;
    epoch.current += 1;
    wasActive.current = false; // the activation effect below loads for the new identity
    busy.current = new Set();
    pendingStore.current = {};
    draftsRef.current = {};
    createKey.current = null;
    creatingRef.current = false;
    faqLoadedAt.current = 0;
    intakeFilesRef.current = [];
    setView("home");
    setActiveId(null);
    setList(EMPTY_LIST);
    setChats({});
    setDrafts({});
    setUnread(0);
    setRestaurants([]);
    // what a visitor may see (categories, FAQs) depends on who they are
    setMeta(null);
    setFaq({ status: "idle", categories: [], items: [], error: null });
    setFaqNav({ category: null, key: null, outcome: null });
    setFaqDetails({});
    intakeRef.current = EMPTY_INTAKE;
    setIntakeState(EMPTY_INTAKE);
    setIntakeFiles([]);
    setCreating(false);
    setPrefs({ status: "idle", email: true, inApp: true, error: null });
    // Tell the user when an identity switch (sign-out, another account) wiped
    // the view; confirming a stored session at start-up (pending -> user) is not one.
    const previous = previousIdentity.current;
    previousIdentity.current = identity;
    const settled = (value) => !value.startsWith("pending:");
    setNotice(previous !== identity && settled(previous) && settled(identity) ? { type: "info", text: live.current.t.sessionChanged } : null);
  }, [identity]);

  // ---- meta (categories), once, when first opened ---------------------------
  useEffect(() => {
    if (!open || meta || mode === "pending") return undefined;
    const controller = new AbortController();
    fetchCopilotMetaAPI(controller.signal, { noAuth: mode === "guest" }).then((data) => setMeta(data), () => {});
    return () => controller.abort();
  }, [open, meta, mode]);

  // ---- a half-written request survives a page refresh (sessionStorage, this tab only) -----------
  useEffect(() => {
    if (mode === "pending") return;
    const saved = loadIntakeDraft(identity);
    if (saved && !hasProgress(intakeRef.current) && intakeRef.current.status === "filling") {
      intakeRef.current = { ...EMPTY_INTAKE, ...saved, status: "filling", error: null, confirmCancel: false };
      setIntakeState(intakeRef.current);
    }
  }, [identity, mode]);

  // ---- unread badge -----------------------------------------------------------
  const refreshUnread = useCallback(async () => {
    const started = epoch.current;
    const current = live.current.mode;
    if (busy.current.has("unread")) return;
    busy.current.add("unread");
    try {
      if (current === "user") {
        const data = await fetchCopilotUnreadAPI();
        if (!stale(started)) setUnread(data.unreadMessages || 0);
      } else if (current === "guest") {
        let total = 0;
        for (const entry of listGuestConversations().slice(0, GUEST_BADGE_MAX)) {
          try {
            const data = await fetchCopilotUnreadAPI({ guestToken: entry.token, conversation: entry.id });
            total += data.unreadMessages || 0;
          } catch (error) {
            if (error.status === 404) removeGuestConversation(entry.id);
          }
        }
        if (!stale(started)) setUnread(total);
      }
    } catch {
      // the badge is best effort; the list shows real errors
    } finally {
      busy.current.delete("unread");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- conversation list --------------------------------------------------------
  const loadGuestList = async (started) => {
    const entries = listGuestConversations();
    const results = await Promise.all(
      entries.map((entry) =>
        getCopilotConversationAPI(entry.id, { guestToken: entry.token, limit: 1 }).then(
          (data) => data.conversation,
          (error) => {
            if (error.status === 404) removeGuestConversation(entry.id);
            return error.status === 404 ? null : { failed: true };
          },
        ),
      ),
    );
    if (stale(started)) return;
    if (results.some((r) => r?.failed)) throw new CopilotApiError("failed");
    setList({ items: results.filter(Boolean).sort(byActivity), status: "ready", error: null, hasMore: false, cursor: null, loadingMore: false });
  };

  const loadList = useCallback(
    async ({ silent = false } = {}) => {
      const started = epoch.current;
      const current = live.current.mode;
      if (current === "pending" || busy.current.has("list")) return;
      busy.current.add("list");
      if (!silent) setList((prev) => ({ ...prev, status: prev.items.length ? prev.status : "loading", error: null }));
      try {
        if (current === "guest") {
          await loadGuestList(started);
        } else {
          const data = await listCopilotConversationsAPI({ limit: PAGE });
          if (stale(started)) return;
          setList((prev) => {
            // Keep pages already loaded beyond the first.
            const fresh = new Map(data.conversations.map((c) => [c.id, c]));
            const older = prev.items.filter((c) => !fresh.has(c.id) && new Date(c.lastActivityAt) < new Date(data.conversations.at(-1)?.lastActivityAt || 0));
            return {
              items: [...data.conversations, ...older].sort(byActivity),
              status: "ready",
              error: null,
              hasMore: older.length ? prev.hasMore : data.hasMore,
              cursor: older.length ? prev.cursor : data.nextCursor,
              loadingMore: false,
            };
          });
        }
      } catch (error) {
        if (!stale(started) && !silent) setList((prev) => ({ ...prev, status: prev.items.length ? "ready" : "error", error: errorMessage(error, live.current.t) }));
      } finally {
        busy.current.delete("list");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const loadMore = useCallback(async () => {
    const started = epoch.current;
    const { cursor, hasMore, loadingMore } = live.current.list;
    if (!hasMore || !cursor || loadingMore || live.current.mode !== "user") return;
    setList((prev) => ({ ...prev, loadingMore: true }));
    try {
      const data = await listCopilotConversationsAPI({ cursor, limit: PAGE });
      if (stale(started)) return;
      setList((prev) => {
        const known = new Set(prev.items.map((c) => c.id));
        return { ...prev, items: [...prev.items, ...data.conversations.filter((c) => !known.has(c.id))], hasMore: data.hasMore, cursor: data.nextCursor, loadingMore: false };
      });
    } catch (error) {
      if (!stale(started)) setList((prev) => ({ ...prev, loadingMore: false, error: errorMessage(error, live.current.t) }));
    }
  }, []);

  // ---- a conversation --------------------------------------------------------
  const markRead = useCallback(
    async (id) => {
      const started = epoch.current;
      if (busy.current.has(`read:${id}`)) return;
      busy.current.add(`read:${id}`);
      try {
        const data = await markCopilotReadAPI(id, { guestToken: tokenFor(id) });
        if (stale(started)) return;
        patchChat(id, (chat) => ({ conversation: { ...(chat?.conversation || {}), unreadCount: data.unreadCount } }));
        patchListItem(id, { unreadCount: data.unreadCount });
        if (data.marked) setUnread((n) => Math.max(0, n - data.marked));
      } catch {
        // retried by the next refresh
      } finally {
        busy.current.delete(`read:${id}`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patchChat, patchListItem],
  );

  const refreshChat = useCallback(
    async (id, { silent = false } = {}) => {
      const started = epoch.current;
      const key = `chat:${id}`;
      if (live.current.mode === "pending" || busy.current.has(key)) return;
      // A message in flight is reconciled by its own response.
      if ((pendingStore.current[id] || []).some((p) => p.status === "sending")) return;
      busy.current.add(key);
      if (!silent) patchChat(id, (chat) => ({ status: chat?.messages?.length ? "ready" : "loading", error: null }));
      try {
        const data = await getCopilotConversationAPI(id, { guestToken: tokenFor(id), limit: 30 });
        if (stale(started)) return;
        patchChat(id, (chat) => ({
          conversation: data.conversation,
          messages: mergeMessages(chat?.messages || [], data.messages),
          hasMore: chat?.messages?.length ? chat.hasMore : data.hasMore,
          nextBefore: chat?.messages?.length ? chat.nextBefore : data.nextBefore,
          status: "ready",
          error: null,
        }));
        patchListItem(id, data.conversation);
        if (data.conversation.unreadCount > 0 && live.current.open && live.current.visible && live.current.activeId === id) markRead(id);
      } catch (error) {
        if (stale(started)) return;
        if (error.status === 404 || error.status === 401) {
          if (live.current.mode === "guest") expire(id);
          else patchChat(id, { status: "notfound" });
        } else if (!silent) {
          patchChat(id, (chat) => ({ status: chat?.messages?.length ? "ready" : "error", error: errorMessage(error, live.current.t) }));
        }
      } finally {
        busy.current.delete(key);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patchChat, patchListItem, markRead, expire],
  );

  const loadEarlier = useCallback(
    async (id) => {
      const started = epoch.current;
      const chat = chats[id];
      if (!chat?.hasMore || chat.loadingEarlier) return;
      patchChat(id, { loadingEarlier: true });
      try {
        const data = await getCopilotConversationAPI(id, { guestToken: tokenFor(id), before: chat.nextBefore, limit: 30 });
        if (stale(started)) return;
        patchChat(id, (c) => ({ messages: mergeMessages(c.messages, data.messages), hasMore: data.hasMore, nextBefore: data.nextBefore, loadingEarlier: false, prepended: Date.now() }));
      } catch (error) {
        if (!stale(started)) patchChat(id, { loadingEarlier: false, error: errorMessage(error, live.current.t) });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chats, patchChat],
  );

  // ---- navigation ------------------------------------------------------------
  const openConversation = useCallback(
    (id) => {
      setActiveId(id);
      setView("chat");
      patchChat(id, (chat) => ({ status: chat?.messages?.length ? "ready" : "loading", pending: chat?.pending || [], messages: chat?.messages || [] }));
      refreshChat(id, { silent: true });
    },
    [patchChat, refreshChat],
  );

  const showList = useCallback(() => {
    setView("list");
    setActiveId(null);
    loadList({ silent: true });
  }, [loadList]);

  const showHome = useCallback(() => {
    setView("home");
    setActiveId(null);
  }, []);

  // Header "back": a conversation returns to the conversation list, everything else to the welcome screen.
  const goBack = useCallback(() => {
    if (live.current.view === "chat") showList();
    else showHome();
  }, [showHome, showList]);

  // ---- sending ---------------------------------------------------------------
  const setPending = useCallback((id, fn) => {
    pendingStore.current[id] = fn(pendingStore.current[id] || []);
    patchChat(id, { pending: pendingStore.current[id] });
  }, [patchChat]);

  const deliver = useCallback(
    async (id, key) => {
      const started = epoch.current;
      const entry = (pendingStore.current[id] || []).find((p) => p.key === key);
      if (!entry) return;
      setPending(id, (items) => items.map((p) => (p.key === key ? { ...p, status: "sending", error: null } : p)));
      const guestToken = tokenFor(id);
      try {
        const uploaded = [...entry.uploaded];
        for (const file of entry.files.slice(uploaded.length)) {
          const result = await uploadCopilotAttachmentAPI(id, file, { guestToken });
          uploaded.push(result.attachment.id);
          setPending(id, (items) => items.map((p) => (p.key === key ? { ...p, uploaded: [...uploaded] } : p)));
        }
        const result = await sendCopilotMessageAPI(id, { text: entry.text, attachments: uploaded, clientMessageId: key }, { guestToken });
        if (stale(started)) return;
        setPending(id, (items) => items.filter((p) => p.key !== key));
        patchChat(id, (chat) => {
          const current = chat?.conversation;
          const reopened = current && current.status !== "open";
          return {
            messages: mergeMessages(chat?.messages || [], [result.message]),
            conversation: current ? { ...current, status: reopened ? "open" : current.status, lastMessage: { text: entry.text || "[Attachment]", senderType: "customer", at: result.message.createdAt }, lastActivityAt: result.message.createdAt } : current,
            sentAt: Date.now(),
          };
        });
        patchListItem(id, { lastMessage: { text: entry.text || "[Attachment]", senderType: "customer", at: result.message.createdAt }, lastActivityAt: result.message.createdAt, status: "open" });
      } catch (error) {
        if (stale(started)) return;
        if (error.status === 404 && live.current.mode === "guest") {
          expire(id);
          return;
        }
        setPending(id, (items) => items.map((p) => (p.key === key ? { ...p, status: "failed", error: errorMessage(error, live.current.t) } : p)));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patchChat, patchListItem, expire],
  );

  const send = useCallback(
    (id) => {
      const draft = draftsRef.current[id] || EMPTY_DRAFT;
      const text = (draft.text || "").trim();
      if ((!text && !draft.files.length) || text.length > 5000) return;
      const key = uuid();
      setPending(id, (items) => [...items, { key, text, files: draft.files, uploaded: [], status: "sending", error: null, createdAt: new Date().toISOString() }]);
      setDraft(id, { text: "", files: [] });
      deliver(id, key);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deliver, setDraft],
  );

  const retry = useCallback((id, key) => deliver(id, key), [deliver]);
  const discardPending = useCallback((id, key) => setPending(id, (items) => items.filter((p) => p.key !== key)), [setPending]);

  // ---- Common questions (FAQ) ----------------------------------------------------------------------
  // What a visitor may see is decided by the server; guests never send a session token for it.
  const loadFaqs = useCallback(async ({ force = false } = {}) => {
    const started = epoch.current;
    if (live.current.mode === "pending" || busy.current.has("faqs")) return;
    if (!force && faqLoadedAt.current && Date.now() - faqLoadedAt.current < FAQ_REFRESH_MS) return;
    busy.current.add("faqs");
    setFaq((prev) => ({ ...prev, status: prev.items.length ? "ready" : "loading", error: null }));
    try {
      const data = await fetchCopilotFaqsAPI({ noAuth: live.current.mode === "guest" });
      if (stale(started)) return;
      faqLoadedAt.current = Date.now();
      setFaq({ status: "ready", categories: data.categories || [], items: data.faqs || [], error: null });
    } catch (error) {
      if (!stale(started)) setFaq((prev) => ({ ...prev, status: prev.items.length ? "ready" : "error", error: errorMessage(error, live.current.t) }));
    } finally {
      busy.current.delete("faqs");
    }
  }, []);

  const loadFaqDetail = useCallback(async (key) => {
    const started = epoch.current;
    if (!key || live.current.mode === "pending") return;
    setFaqDetails((prev) => (prev[key]?.status === "ready" || prev[key]?.status === "loading" ? prev : { ...prev, [key]: { status: "loading", data: null, error: null } }));
    try {
      const data = await fetchCopilotFaqAPI(key, { noAuth: live.current.mode === "guest" });
      if (!stale(started)) setFaqDetails((prev) => ({ ...prev, [key]: { status: "ready", data: data.faq, error: null } }));
    } catch (error) {
      if (!stale(started)) setFaqDetails((prev) => ({ ...prev, [key]: { status: "error", data: null, error: errorMessage(error, live.current.t) } }));
    }
  }, []);

  const showFaq = useCallback(() => {
    setView("faq");
    setActiveId(null);
    setFaqNav({ category: null, key: null, outcome: null }); // always start from the topics
    loadFaqs();
  }, [loadFaqs]);

  const faqOpenCategory = useCallback((category) => setFaqNav({ category, key: null, outcome: null }), []);
  const faqOpenQuestion = useCallback(
    (key) => {
      setFaqNav((prev) => ({ ...prev, key, outcome: null }));
      loadFaqDetail(key);
    },
    [loadFaqDetail],
  );
  // One step back inside the FAQ (answer -> questions -> topics).
  const faqBack = useCallback(() => setFaqNav((prev) => (prev.key ? { category: prev.category, key: null, outcome: null } : { category: null, key: null, outcome: null })), []);
  const faqRestart = useCallback(() => setFaqNav({ category: null, key: null, outcome: null }), []);

  // Anonymous visitors vote with a random per-browser id; the server counts one vote per visitor.
  const sendFaqFeedback = useCallback((key, helpful) => {
    const guest = live.current.mode === "guest";
    sendCopilotFaqFeedbackAPI(key, { helpful, sessionId: guest ? faqSessionId() : undefined }, { noAuth: guest }).catch(() => {});
  }, []);

  const faqHelpful = useCallback(
    (key) => {
      sendFaqFeedback(key, true);
      setFaqNav((prev) => ({ ...prev, outcome: "helpful" }));
    },
    [sendFaqFeedback],
  );

  // ---- guided support request (intake) ------------------------------------------------------------
  const writeIntake = useCallback((next) => {
    intakeRef.current = next;
    setIntakeState(next);
    if (next.status === "filling" && hasProgress(next)) saveIntakeDraft(live.current.identity, persistable(next));
    else if (next.status !== "submitting") clearIntakeDraft(live.current.identity);
  }, []);
  const patchIntake = useCallback((patch) => writeIntake({ ...intakeRef.current, ...(typeof patch === "function" ? patch(intakeRef.current) : patch) }), [writeIntake]);

  // Who is asking and what the account already tells us.
  const intakeContext = () => {
    const { mode: m, user: u, restaurants: list_ } = live.current;
    const cur = intakeRef.current;
    const accountPhone = m === "user" ? normalizePhone(u?.phone || "") : null;
    const needsRestaurant = m === "user" && list_.length > 1;
    const categoryKnown = Boolean(cur.faq && cur.category);
    return {
      mode: m,
      accountPhone,
      accountPhoneValid: Boolean(accountPhone),
      needsRestaurant,
      categoryKnown,
      steps: applicableSteps({ mode: m, categoryKnown, accountPhoneValid: Boolean(accountPhone), needsRestaurant }),
      editable: editableSteps({ mode: m, accountPhoneValid: Boolean(accountPhone), needsRestaurant }),
    };
  };

  // Categories this visitor may choose: the server already filters /meta, this keeps the fallback honest.
  const visibleCategories = (meta?.categories || CATEGORY_FALLBACK).filter((cat) => cat.audience !== "authenticated" || mode === "user");

  const loadRestaurants = useCallback(() => {
    if (live.current.mode === "user" && getPolicy({ isAuthenticated: true, user: live.current.user }).has(menuPermissions.audience.restaurantManager)) {
      fetchCopyRestaurantsAPI().then((rows) => setRestaurants(rows.map((r) => ({ value: r.id, label: r.businessName || r.id }))), () => {});
    }
  }, []);

  const resetCreateAttempt = useCallback(() => {
    createKey.current = null;
  }, []);

  /**
   * Opens the guided request. `faq` ({ key, supportCategory, question, answer }) carries an unhelpful
   * FAQ into it: the category is pre-filled and the customer is not asked the question again.
   * Without `faq` an unfinished draft is resumed instead of being thrown away.
   */
  const startIntake = useCallback(
    ({ faq: context = null } = {}) => {
      const cur = intakeRef.current;
      setView("intake");
      setActiveId(null);
      loadRestaurants();
      if (!context && cur.status === "filling" && hasProgress(cur)) return;
      const allowed = (live.current.meta?.categories || CATEGORY_FALLBACK).filter((cat) => cat.audience !== "authenticated" || live.current.mode === "user");
      const preset = context && allowed.some((cat) => cat.value === context.supportCategory) ? context.supportCategory : "";
      createKey.current = null;
      intakeFilesRef.current = [];
      setIntakeFiles([]);
      writeIntake({
        ...EMPTY_INTAKE,
        faq: context ? { key: context.key, category: context.category, question: context.question, answer: context.answer } : null,
        category: preset,
        step: preset ? STEPS.MESSAGE : STEPS.CATEGORY,
      });
    },
    [loadRestaurants, writeIntake],
  );

  // "No, I need help": vote, then continue into the guided request with the FAQ as context.
  const faqNotHelpful = useCallback(
    (key) => {
      sendFaqFeedback(key, false);
      const detail = faqDetails[key]?.data;
      startIntake({ faq: detail ? { key, supportCategory: detail.supportCategory, category: detail.category, question: detail.question, answer: detail.answer } : { key } });
    },
    [faqDetails, sendFaqFeedback, startIntake],
  );

  const valueOfStep = (cur, step) => (step === STEPS.MESSAGE ? cur.message : step === STEPS.NAME ? cur.name : step === STEPS.EMAIL ? cur.email : step === STEPS.PHONE ? cur.phone : "");

  const nextStepAfter = (step) => {
    const { steps } = intakeContext();
    return steps[steps.indexOf(step) + 1] || STEPS.REVIEW;
  };

  /** Validates the answer to `step`; on success stores it and moves on. Returns { error } or {}. */
  const answerIntake = useCallback(
    (step, raw) => {
      const cur = intakeRef.current;
      const text = live.current.g;
      let patch;
      if (step === STEPS.CATEGORY) {
        if (!(live.current.meta?.categories || CATEGORY_FALLBACK).some((cat) => cat.value === raw && (cat.audience !== "authenticated" || live.current.mode === "user"))) return { error: text.errCategory };
        patch = { category: raw };
      } else if (step === STEPS.RESTAURANT) {
        patch = { restaurant: typeof raw === "string" ? raw : "" };
      } else if (step === STEPS.MESSAGE) {
        const message = cleanMessage(raw);
        if (!message) return { error: text.errMessageEmpty };
        if (message.length > MESSAGE_MAX) return { error: text.errMessageLong(MESSAGE_MAX) };
        patch = { message };
      } else if (step === STEPS.NAME) {
        const name = cleanName(raw);
        if (!name) return { error: text.errName };
        patch = { name };
      } else if (step === STEPS.EMAIL) {
        const email = normalizeEmail(raw);
        if (!email) return { error: text.errEmail };
        patch = { email };
      } else if (step === STEPS.PHONE) {
        const phone = normalizePhone(raw);
        if (!phone) return { error: text.errPhone };
        patch = { phone };
      } else {
        return {};
      }
      createKey.current = null; // changed content = a new request
      patchIntake({ ...patch, input: "", step: cur.editing ? STEPS.REVIEW : nextStepAfter(step), editing: false, error: null, confirmCancel: false });
      return {};
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patchIntake],
  );

  const setIntakeInput = useCallback((input) => patchIntake({ input, error: null }), [patchIntake]);

  const intakeBack = useCallback(() => {
    const cur = intakeRef.current;
    const { steps } = intakeContext();
    if (cur.step === STEPS.EDIT || cur.editing) {
      patchIntake({ step: STEPS.REVIEW, editing: false, input: "", error: null });
      return;
    }
    const index = steps.indexOf(cur.step);
    if (index <= 0) {
      setView("home");
      return;
    }
    const previous = steps[index - 1];
    patchIntake({ step: previous, input: valueOfStep(cur, previous), error: null, confirmCancel: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchIntake]);

  const intakeEdit = useCallback(() => patchIntake({ step: STEPS.EDIT, error: null }), [patchIntake]);
  const intakeEditField = useCallback((step) => patchIntake((cur) => ({ step, editing: true, input: valueOfStep(cur, step), error: null })), [patchIntake]);

  const intakeCancel = useCallback(() => {
    if (hasProgress(intakeRef.current)) patchIntake({ confirmCancel: true });
    else setView("home");
  }, [patchIntake]);
  const intakeKeepGoing = useCallback(() => patchIntake({ confirmCancel: false }), [patchIntake]);
  const intakeDiscard = useCallback(() => {
    createKey.current = null;
    intakeFilesRef.current = [];
    setIntakeFiles([]);
    writeIntake(EMPTY_INTAKE);
    setView("home");
  }, [writeIntake]);

  const addIntakeFiles = useCallback((files) => {
    intakeFilesRef.current = files;
    setIntakeFiles(files);
  }, []);

  // ---- creating --------------------------------------------------------------
  // Resolves to { error, network } or { id, reference, hasAccess }. One attempt shares one
  // Idempotency-Key until it succeeds, so a retried or double-clicked submit cannot create two.
  const createConversation = useCallback(
    async (form) => {
      if (creatingRef.current) return { error: null, busy: true };
      const started = epoch.current;
      const asGuest = live.current.mode === "guest";
      const info = intakeContext();
      creatingRef.current = true;
      setCreating(true);
      createKey.current = createKey.current || uuid();
      const restaurant = form.restaurant || (live.current.restaurants.length === 1 ? live.current.restaurants[0].value : "");
      const body = {
        category: form.category,
        // language of the e-mails the customer will receive
        lang: live.current.lang === "en" ? "en" : "ar",
        message: form.message.trim(),
        ...(form.faq?.key ? { faq: form.faq.key } : {}),
        ...(asGuest ? { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() } : { ...(restaurant ? { restaurant } : {}), ...(!info.accountPhoneValid && form.phone ? { phone: form.phone } : {}) }),
      };
      try {
        const data = await createCopilotConversationAPI(body, { idempotencyKey: createKey.current, asGuest });
        if (stale(started)) return { error: null };
        createKey.current = null;
        const conversation = data.conversation;
        // A replayed guest creation never re-issues the access key: the request exists, but this
        // browser cannot open it (the e-mailed link can).
        let hasAccess = true;
        if (asGuest) {
          if (data.guestAccess?.token) saveGuestConversation({ id: conversation.id, token: data.guestAccess.token, reference: conversation.reference, subject: conversation.subject, createdAt: new Date().toISOString() });
          else hasAccess = false;
        }
        if (hasAccess) {
          setList((prev) => ({ ...prev, items: [conversation, ...prev.items.filter((c) => c.id !== conversation.id)], status: "ready" }));
          patchChat(conversation.id, { conversation, messages: [], pending: [], status: "loading" });
        }
        const files = hasAccess ? intakeFilesRef.current : [];
        intakeFilesRef.current = [];
        setIntakeFiles([]);
        // Attachments need the conversation to exist: sent as the first follow-up.
        if (files.length) {
          const key = uuid();
          setPending(conversation.id, (items) => [...items, { key, text: "", files, uploaded: [], status: "sending", error: null, createdAt: new Date().toISOString() }]);
          deliver(conversation.id, key);
        }
        return { id: conversation.id, reference: conversation.reference, hasAccess, attachments: files.length > 0 };
      } catch (error) {
        return { error: errorMessage(error, live.current.t), status: error.status, network: Boolean(error.network), message: error.status === 400 ? error.message : null };
      } finally {
        creatingRef.current = false;
        if (!stale(started)) setCreating(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patchChat, deliver],
  );

  // "Send Request": nothing exists (and nothing is e-mailed) until the backend confirms the creation.
  const submitIntake = useCallback(async () => {
    const cur = intakeRef.current;
    if (creatingRef.current || cur.status === "submitting") return;
    patchIntake({ status: "submitting", error: null, confirmCancel: false });
    const result = await createConversation(cur);
    if (result.busy) return;
    if (result.error) {
      patchIntake({ status: "filling", error: { text: result.network ? live.current.g.submitNoConnection : result.message || (result.status === 429 ? result.error : live.current.g.submitFailed), status: result.status || null } });
      return;
    }
    if (!result.id) return; // identity changed meanwhile
    clearIntakeDraft(live.current.identity);
    intakeRef.current = { ...EMPTY_INTAKE, step: STEPS.REVIEW, status: "done", name: cur.name || live.current.user?.name || "", faq: cur.faq, done: { id: result.id, reference: result.reference, hasAccess: result.hasAccess, attachments: result.attachments } };
    setIntakeState(intakeRef.current);
  }, [createConversation, patchIntake]);

  // Leaves the confirmation (or an abandoned flow) for the welcome screen.
  const intakeFinish = useCallback(() => {
    createKey.current = null;
    writeIntake(EMPTY_INTAKE);
    setView("home");
  }, [writeIntake]);

  // Opens the conversation that was just created.
  const intakeOpenConversation = useCallback(
    (id) => {
      createKey.current = null;
      writeIntake(EMPTY_INTAKE);
      openConversation(id);
    },
    [openConversation, writeIntake],
  );

  // ---- notification preferences (signed-in users) ---------------------------------------
  const showSettings = useCallback(() => {
    if (live.current.mode !== "user") return;
    setView("settings");
    setActiveId(null);
    const started = epoch.current;
    setPrefs((prev) => ({ ...prev, status: "loading", error: null }));
    fetchCopilotPreferencesAPI().then(
      (data) => !stale(started) && setPrefs({ status: "ready", error: null, ...data.preferences }),
      (error) => !stale(started) && setPrefs((prev) => ({ ...prev, status: "error", error: errorMessage(error, live.current.t) })),
    );
  }, []);

  // Saves one switch; the screen shows the new value at once and reverts it if the save fails.
  const setPreference = useCallback(async (key, value) => {
    const started = epoch.current;
    const previous = prefs[key];
    setPrefs((prev) => ({ ...prev, [key]: value, error: null }));
    try {
      const data = await updateCopilotPreferencesAPI({ [key]: value });
      if (!stale(started)) setPrefs((prev) => ({ ...prev, ...data.preferences }));
    } catch (error) {
      if (!stale(started)) setPrefs((prev) => ({ ...prev, [key]: previous, error: errorMessage(error, live.current.t) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs]);

  // ---- deep links (e-mail links, notification clicks) --------------------------------------
  // `accessToken` is the single-use token of an e-mailed guest link: it is exchanged for
  // a session of this browser and stored like a normal guest token. Everything else
  // opens the conversation exactly as the inbox would, so the backend still decides.
  const openLink = useCallback(async ({ conversationId, accessToken = null }) => {
    const started = epoch.current;
    const current = live.current;
    if (current.mode === "pending" || !conversationId) return;
    setNotice(null);
    if (accessToken) {
      try {
        const data = await redeemCopilotEmailLinkAPI(conversationId, accessToken);
        if (stale(started)) return;
        saveGuestConversation({ id: conversationId, token: data.guestAccess.token, reference: data.conversation.reference, subject: data.conversation.subject, createdAt: new Date().toISOString() });
      } catch (error) {
        if (stale(started)) return;
        // used, expired or wrong: say so, and fall through (they may already hold access)
        if (!getGuestToken(conversationId) && current.mode === "guest") {
          setNotice({ type: "warning", text: current.t.linkExpired, signIn: true });
          setView("list");
          loadList({ silent: false });
          return;
        }
      }
    }
    if (current.mode === "guest" && !getGuestToken(conversationId)) {
      setNotice({ type: "warning", text: current.t.linkNeedsAccess, signIn: true });
      setView("list");
      loadList({ silent: false });
      return;
    }
    if (current.mode === "user") {
      setList((prev) => prev); // the list refreshes when the user goes back
      patchChat(conversationId, { status: "loading", messages: [], pending: [] });
    }
    openConversation(conversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openConversation, loadList, patchChat]);

  // ---- loading & polling schedule ---------------------------------------------
  useEffect(() => {
    const active = open && visible && mode !== "pending";
    if (active && !wasActive.current) {
      if (live.current.view === "chat" && live.current.activeId) refreshChat(live.current.activeId, { silent: true });
      else loadList({ silent: false });
      refreshUnread();
    }
    wasActive.current = active;
  }, [open, visible, mode, identity, refreshChat, loadList, refreshUnread]);

  useEffect(() => {
    if (mode === "pending") return undefined;
    const open_ = open && visible;
    const timer = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      const { view: v, activeId: a } = live.current;
      if (open_) {
        if (v === "chat" && a) refreshChat(a, { silent: true });
        else if (v === "list") loadList({ silent: true });
      }
      refreshUnread();
    }, open_ ? POLL_OPEN_MS : POLL_CLOSED_MS);
    return () => clearInterval(timer);
  }, [open, visible, mode, identity, refreshChat, loadList, refreshUnread]);

  // Returning to the tab: refresh once (throttled).
  useEffect(() => {
    if (mode === "pending") return undefined;
    const onReturn = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastFocusAt.current < FOCUS_MIN_MS) return;
      lastFocusAt.current = Date.now();
      const { view: v, activeId: a, open: o } = live.current;
      if (o && v === "chat" && a) refreshChat(a, { silent: true });
      else if (o && v === "list") loadList({ silent: true });
      refreshUnread();
    };
    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("focus", onReturn);
    return () => {
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("focus", onReturn);
    };
  }, [mode, identity, refreshChat, loadList, refreshUnread]);

  // Initial badge once the identity is known (one request; no list load).
  useEffect(() => {
    if (mode !== "pending") refreshUnread();
  }, [identity, mode, refreshUnread]);

  // Read receipts while a conversation is on screen.
  useEffect(() => {
    if (!open || !visible || view !== "chat" || !activeId) return;
    if ((chats[activeId]?.conversation?.unreadCount || 0) > 0) markRead(activeId);
  }, [open, visible, view, activeId, chats, markRead]);

  const hasGuestConversations = mode === "guest" && listGuestConversations().length > 0;
  return {
    t, g, mode, view, activeId, list, chats, drafts, unread, meta, restaurants, notice, creating,
    categories: meta?.categories || null,
    visibleCategories,
    canListConversations: mode === "user" || hasGuestConversations,
    dismissNotice: () => setNotice(null),
    setDraft, send, retry, discardPending, resetCreateAttempt,
    // guided chat
    showHome, goBack,
    faq, faqNav, faqDetails, showFaq, loadFaqs, loadFaqDetail, faqOpenCategory, faqOpenQuestion, faqBack, faqRestart, faqHelpful, faqNotHelpful,
    intake, intakeFiles, intakeInfo: intakeContext(), startIntake, answerIntake, setIntakeInput, intakeBack, intakeEdit, intakeEditField, intakeCancel, intakeKeepGoing, intakeDiscard, addIntakeFiles, submitIntake, intakeFinish, intakeOpenConversation,
    openConversation, openLink, showList, showSettings, setPreference, prefs, setView, loadList, loadMore, refreshChat, loadEarlier,
  };
}

export { EMPTY_DRAFT };
